import React, { useRef, useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import infoIco from 'assets/images/i-circle.svg';
import Verified from 'assets/images/verified.gif';

import UserHttp from '../services/profile.service';
import { useCooldownTimer } from '../hooks/useCooldownTimer';
import ConfirmBankDetailsModal from './ConfirmBankDetailsModal';
import ConfirmBankDocumentModal from './ConfirmBankDocumentModal';
interface BankFormProps {
  customer: any;
  user: any;
  setCustomer: (customer: any) => void;
}

type BankUIAction =
  | 'SUBMIT'
  | 'EDIT'
  | 'UPLOAD_DOC'
  | 'PENDING'
  | 'VERIFIED'
  | 'LOCKED'
  | 'NONE';

const primaryBtnStyle = (variant: string = 'primary') => {
  const colorMap: Record<string, any> = {
    primary: {
      backgroundColor: '#99834D',
      borderColor: '#99834D',
      color: '#ffffff',
    },
    warning: {
      backgroundColor: '#F0AD4E',
      borderColor: '#EEA236',
      color: '#ffffff',
    },
    success: {
      backgroundColor: '#28A745',
      borderColor: '#218838',
      color: '#ffffff',
    },
    info: {
      backgroundColor: '#99834D',
      borderColor: '#99834D',
      color: '#ffffff',
    },
    secondary: {
      backgroundColor: '#E9ECEF',
      borderColor: '#CED4DA',
      color: '#495057',
    },
    dark: {
      backgroundColor: '#343A40',
      borderColor: '#343A40',
      color: '#ffffff',
    },
  };

  return {
    height: 42,
    minWidth: 180,
    padding: '0 20px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '14px',
    letterSpacing: '0.4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    ...colorMap[variant],
  };
};

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const getErrorMessage = (error: any) =>
  typeof error === 'string' ? error : JSON.stringify(error);

const sanitizeBankDetails = (values: any) => ({
  name: values.nameAsOnBankAccount.trim().toUpperCase(),
  accountNo: values.bankAccount.replace(/\D/g, '').trim(),
  ifsc: values.ifsc.trim().toUpperCase(),
  bank: values.bankName,
});

const BankForm: React.FC<BankFormProps> = ({ customer, user, setCustomer }) => {
  const formikRef = useRef<any>(null);

  const [isIfscVerified, setIsIfscVerified] = useState(false);
  const [isBankSearching, setIsBankSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifiedAndSaved, setIsVerifiedAndSaved] = useState(false);
  const [ifscApiError, setIfscApiError] = useState<string | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // const [hasConsent, setHasConsent] = useState(false);

  const [bankProofFile, setBankProofFile] = useState<File | null>(null);
  const [bankProofPreview, setBankProofPreview] = useState<string | null>(null);

  const [showUploadModal, setShowUploadModal] = useState(false);

  // ── NEW: confirm account number state ──────────────────────────────────────
  // Tracks whether the user has started typing in the confirm field.
  // Once they do, the primary account number field is frozen.
  const [confirmAccountNo, setConfirmAccountNo] = useState('');
  const [confirmAccountNoTouched, setConfirmAccountNoTouched] = useState(false);
  const [isAccountNoFrozen, setIsAccountNoFrozen] = useState(false);
  const [showAccountNo, setShowAccountNo] = useState(false);


  const maskAccountNo = (accountNo: string) => {
    if (!accountNo) return '';
    if (accountNo.length <= 3) return accountNo;
    return '•'.repeat(accountNo.length - 3) + accountNo.slice(-3);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const existingBankDetails = customer?.bankDetails || {};

  const [newBankDetails, setNewBankDetails] = useState({
    bankAccount: existingBankDetails.accountNo || '',
    nameAsOnBankAccount:
      existingBankDetails.name || customer?.kyc?.pan?.data?.name || '',
    ifsc: existingBankDetails.ifsc || '',
    bankName: existingBankDetails.bank || '',
  });

  const {
    timeLeft,
    isActive: bankPending,
    start: startCooldownTimer,
  } = useCooldownTimer('bankVerificationTimerEnd');

  const getBankUIAction = (): BankUIAction => {
    const bank = customer?.bankDetails ?? {};
    const attemptsLeft = customer?.bankDetailsAddCount ?? 0;
    const bankVerification = customer?.bankVerification;

    const hasNoBankDetails =
      !bank?.accountNo && !bank?.ifsc && !bank?.name;

    if (hasNoBankDetails) return 'SUBMIT';

    if (bank.state === 'VERIFIED') return 'VERIFIED';

    if (
      bank.state === 'REJECTED' &&
      bank?.bankStatus === 'NAME_MISMATCH' &&
      !bank?.rejectedByAdmin
    ) {
      return 'NONE';
    }

    if (attemptsLeft === 0 && bankVerification?.active === false)
      return 'UPLOAD_DOC';

    if (bankVerification?.attachmentUrl) return 'PENDING';

    if (attemptsLeft > 0 && bank.state !== 'VERIFIED') return 'EDIT';

    if (attemptsLeft === 0 && !bankVerification?.attachmentUrl)
      return 'UPLOAD_DOC';

    return 'PENDING';
  };

  const uiAction = getBankUIAction();
  const areFieldsEditable = ['SUBMIT', 'EDIT'].includes(uiAction);

  // const requiresConsent =
  //   uiAction === 'SUBMIT' && !customer?.bankDetails?.consent;

  const isUiLocked = isProcessing || isBankSearching || isVerifiedAndSaved;

  const canEditFields = areFieldsEditable && !isUiLocked;

  // Whether the confirm account field is shown (only on SUBMIT / EDIT)
  const showConfirmAccountField = areFieldsEditable;

  const isPanVerified = customer?.kyc?.pan?.isVerified === true;

  useEffect(() => {
    if (
      existingBankDetails?.ifsc &&
      existingBankDetails.ifsc === newBankDetails.ifsc
    ) {
      setIsIfscVerified(true);
    }
  }, []);

  useEffect(() => {
    if (!bankPending && timeLeft === 0) {
      UserHttp.getProfile(customer.customerId, user?.userId)
        .then((res) => {
          const updated = res?.data?.data?.customer;
          if (updated) setCustomer(updated);
        })
        .catch(() => { });
    }
  }, [bankPending, timeLeft]);

  // ── Reset confirm-account state whenever the primary account value changes ──
  // (e.g. when the user edits the account number field)
  const resetConfirmAccount = () => {
    setConfirmAccountNo('');
    setConfirmAccountNoTouched(false);
    setIsAccountNoFrozen(false);
  };
  // ───────────────────────────────────────────────────────────────────────────

  const handleBankProofSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    setBankProofFile(file);

    if (['image/heic', 'image/heif'].includes(file.type)) {
      setBankProofPreview(null);
      toast.info('Preview not available for iPhone images.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setBankProofPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const verifyIfscCode = async (
    ifsc: string,
    setFieldValue: any,
    setErrors: any
  ) => {
    if (!ifsc || isUiLocked) return;

    try {
      setIsBankSearching(true);
      setIfscApiError(null);

      const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (!res.ok) throw new Error();

      const data = await res.json();
      if (!data?.BANK) throw new Error();

      setIsIfscVerified(true);
      setFieldValue('bankName', data.BANK);
      setNewBankDetails((p) => ({ ...p, bankName: data.BANK }));
    } catch {
      setIsIfscVerified(false);
      setFieldValue('bankName', '');
      setErrors({ ifsc: 'Invalid IFSC code' });
      setIfscApiError('Invalid IFSC code');
      toast.error('Invalid IFSC code');
    } finally {
      setIsBankSearching(false);
    }
  };

  const handleSubmit = async (values: any, { setErrors }: any) => {
    if (!customer?.kyc?.pan?.isVerified) {
      toast.warning('Please verify PAN first.');
      return;
    }
    await handleVerify(values, setErrors);
  };

  const handleVerify = async (values: any, setErrors: any) => {
    if (isUiLocked) return;

    setIsProcessing(true);
    const start = Date.now();

    try {
      const sanitized = sanitizeBankDetails(values);

      const payload = {
        transactionType: 'VERIFICATION',
        transactionMethod: 'IMPS',
        referenceId: customer.customerId,
        referenceType: 'CUSTOMER',
        customerAccount: {
          accountType: 'BANK',
          ...sanitized,
          // ...(requiresConsent && { consent: true }),
        },
      };

      const res = await UserHttp.accountVerify(payload, user?.userId);
      if (res.status !== 200) throw new Error();

      const profileRes = await UserHttp.getProfile(
        customer.customerId,
        user?.userId
      );

      const updatedCustomer = profileRes?.data?.data?.customer;
      const bank = updatedCustomer?.bankDetails;

      if (updatedCustomer) setCustomer(updatedCustomer);

      if (bank?.state === 'VERIFIED') {
        setIsVerifiedAndSaved(true);
        toast.success('Bank verified successfully.');
      } else {
        startCooldownTimer(60_000);
        toast.info('Verification in progress.');
      }
    } catch {
      toast.error('Verification failed');
    } finally {
      await wait(Math.max(0, 60_000 - (Date.now() - start)));
      setIsProcessing(false);
    }
  };

  const handleUploadBankProof = async () => {
    if (!bankProofFile) return;

    const formData = new FormData();
    formData.append('file', bankProofFile);

    try {
      setIsProcessing(true);
      await UserHttp.uploadBankVerificationDoc(formData);
      toast.success('Bank proof uploaded successfully');

      const profileRes = await UserHttp.getProfile(
        customer.customerId,
        user?.userId
      );

      const updatedCustomer = profileRes?.data?.data?.customer;
      if (updatedCustomer) setCustomer(updatedCustomer);

      setBankProofFile(null);
      setBankProofPreview(null);
      setShowUploadModal(false);
    } catch {
      toast.error('Failed to upload bank proof');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add this useEffect in BankForm, after the existing useEffects

  useEffect(() => {
    const panName = customer?.kyc?.pan?.data?.name;
    if (panName && !existingBankDetails?.name) {
      setNewBankDetails((prev) => ({
        ...prev,
        nameAsOnBankAccount: panName,
      }));
      // Also update Formik field if formikRef is ready
      if (formikRef.current) {
        formikRef.current.setFieldValue('nameAsOnBankAccount', panName);
      }
    }
  }, [customer?.kyc?.pan?.data?.name]);

  /* ───────────────────────── JSX ───────────────────────── */


  // console.log(customer?.bankDetails?.state, "@customer")
  return (
    <>
      <div style={{ position: 'relative' }}>
        <Card className='mt-5 shadow-sm'>
          <Card.Body className='p-4 p-md-5'>
            <Card.Title className='mb-4 fw-semibold fs-4'>
              Bank Details
            </Card.Title>

            <Formik
              innerRef={formikRef}
              enableReinitialize
              initialValues={newBankDetails}
              validationSchema={Yup.object().shape({
                bankAccount: Yup.string()
                  .matches(/^\d{9,18}$/, 'Account number must be 9–18 digits')
                  .required('Account number is required'),
                nameAsOnBankAccount: Yup.string().required('Name is required'),
                ifsc: Yup.string()
                  .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format')
                  .required('IFSC code is required'),
                bankName: Yup.string().required('Bank name is required'),
              })}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                touched,
                values,
                isValid,
                setFieldValue,
                setErrors,
              }) => {
                const isAccountValid = /^\d{9,18}$/.test(values.bankAccount);
                const isIfscFormatValid = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(
                  values.ifsc
                );

                // ── Confirm account number derived state ───────────────────────
                // Confirm field is enabled only when the primary account passes the regex
                const isConfirmFieldEnabled = isAccountValid && canEditFields && !isUiLocked;

                // Match check (only meaningful when confirm has content)
                const confirmMismatch =
                  confirmAccountNoTouched &&
                  confirmAccountNo.length > 0 &&
                  confirmAccountNo !== values.bankAccount;

                const confirmMatch =
                  confirmAccountNoTouched &&
                  confirmAccountNo.length > 0 &&
                  confirmAccountNo === values.bankAccount;
                // ───────────────────────────────────────────────────────────────

                const canSubmit =
                  (uiAction === 'SUBMIT' || uiAction === 'EDIT') &&
                  // (!requiresConsent) &&
                  isAccountValid &&
                  isIfscFormatValid &&
                  isIfscVerified &&
                  values.bankName?.length > 0 &&
                  isValid &&
                  !isProcessing &&
                  !isVerifiedAndSaved &&
                  !bankPending &&
                  // ── NEW: confirm account must match before allowing submit ──
                  confirmMatch;

                const getPrimaryButtonConfig = () => {
                  switch (uiAction) {
                    case 'NONE':
                      return null;

                    case 'SUBMIT':
                      return {
                        label: 'Submit Bank Details',
                        variant: 'primary',
                        disabled: !canSubmit,
                        onClick: () => setShowConfirmModal(true),
                      };

                    case 'EDIT':
                      return {
                        label: 'Edit & Resubmit',
                        variant: 'warning',
                        disabled: !canSubmit,
                        onClick: () => setShowConfirmModal(true),
                      };

                    case 'UPLOAD_DOC':
                      return {
                        label: 'Upload Bank Proof',
                        variant: 'info',
                        disabled: !bankProofFile || isProcessing,
                        onClick: () => setShowUploadModal(true),
                      };

                    case 'PENDING':
                      return {
                        label: isProcessing
                          ? 'Processing…'
                          : 'Verification in progress…',
                        variant: 'info',
                        disabled: true,
                        onClick: undefined,
                      };

                    case 'VERIFIED':
                      return {
                        label: 'Bank Verified',
                        variant: 'success',
                        disabled: true,
                      };

                    case 'LOCKED':
                      return {
                        label: 'Contact Support',
                        variant: 'dark',
                        disabled: true,
                      };

                    default:
                      return null;
                  }
                };

                const buttonConfig = getPrimaryButtonConfig();

                const bank = customer?.bankDetails ?? {};
                const attemptsLeft = customer?.bankDetailsAddCount ?? 0;
                const bankVerification = customer?.bankVerification;

                const showBankRejectedMessage =
                  (uiAction === 'EDIT' || uiAction === 'PENDING') &&
                  attemptsLeft > 0 &&
                  (bank.state === 'PENDING' ||
                    (bank.state === 'REJECTED' &&
                      bank.bankStatus === 'BANK_REJECTED') ||
                    (bank.state === 'REJECTED' &&
                      bank.bankStatus === 'NAME_MISMATCH' &&
                      bank.rejectedByAdmin === true));

                const showNameMismatchMessage =
                  uiAction === 'NONE' &&
                  bank.state === 'REJECTED' &&
                  bank.bankStatus === 'NAME_MISMATCH' &&
                  bank.rejectedByAdmin === false;

                const showBankVerificationMessage =
                  uiAction === 'UPLOAD_DOC' &&
                  attemptsLeft === 0;

                const fallbackUploadMessage =
                  'All attempts have been used. Please upload your bank document (cancelled cheque or bank passbook or statement) to complete verification.';

                return (
                  <Form
                    style={{
                      pointerEvents: isUiLocked ? 'none' : 'auto',
                      opacity: isUiLocked ? 0.7 : 1,
                    }}
                  >
                    {showBankRejectedMessage && (
                      <div
                        className='mb-4 p-4 rounded-3'
                        style={{
                          background: '#FFF4F4',
                          border: '1px solid #F5C2C7',
                          borderLeft: '4px solid #DC3545',
                        }}
                      >
                        <div className='d-flex align-items-start gap-3'>
                          <div style={{ width: 20, height: 20, display: 'flex' }}>
                            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                              <path d='M12 9V13' stroke='#DC2626' strokeWidth='2' strokeLinecap='round' />
                              <circle cx='12' cy='17' r='1' fill='#DC2626' />
                              <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' stroke='#DC2626' strokeWidth='2' strokeLinejoin='round' />
                            </svg>
                          </div>
                          <div>
                            <div className='mb-2' style={{ fontSize: 14, color: '#DC3545' }}>
                              Your bank did not accept the submitted account details. Please review and correct the information before resubmitting.
                            </div>
                            <div className='fw-semibold' style={{ color: attemptsLeft <= 1 ? '#DC3545' : '#856404', fontSize: 14 }}>
                              {attemptsLeft} attempt{attemptsLeft !== 1 && 's'} remaining
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {showNameMismatchMessage && (
                      <div
                        className='mb-4 p-4 rounded-3'
                        style={{
                          background: '#FFF9E6',
                          border: '1px solid #FFE69C',
                          borderLeft: '4px solid #F0AD4E',
                        }}
                      >
                        <div className='d-flex align-items-start gap-3'>
                          <div style={{ width: 20, height: 20, display: 'flex' }}>
                            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                              <circle cx='12' cy='12' r='10' stroke='#F0AD4E' strokeWidth='2' />
                              <path d='M12 10V16' stroke='#F0AD4E' strokeWidth='2' strokeLinecap='round' />
                              <circle cx='12' cy='7' r='1' fill='#F0AD4E' />
                            </svg>
                          </div>
                          <div>
                            <div className='text-muted' style={{ fontSize: 14 }}>
                              The account holder name does not match our records. Our team is reviewing this and will update you shortly.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {showBankVerificationMessage && (
                      <div
                        className='mb-4 p-4 rounded-3'
                        style={{
                          background: '#FFF4F4',
                          border: '1px solid #F5C2C7',
                          borderLeft: '4px solid #DC3545',
                        }}
                      >
                        <div className='d-flex align-items-start gap-3'>
                          <div style={{ width: 20, height: 20, display: 'flex' }}>
                            <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                              <path d='M12 9V13' stroke='#DC2626' strokeWidth='2' strokeLinecap='round' />
                              <circle cx='12' cy='17' r='1' fill='#DC2626' />
                              <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' stroke='#DC2626' strokeWidth='2' strokeLinejoin='round' />
                            </svg>
                          </div>
                          <div>
                            <div className='mb-2' style={{ fontSize: 14, color: '#DC2626' }}>
                              {bankVerification?.message || fallbackUploadMessage}
                            </div>
                            {bankVerification?.uploadDate && (
                              <div className='small text-muted mb-2' style={{ fontSize: 12 }}>
                                Uploaded on: {new Date(bankVerification.uploadDate).toLocaleDateString()}
                              </div>
                            )}
                            {bankVerification?.attachmentUrl && (
                              <div className='mt-2'>
                                <Button size='sm' variant='outline-danger' onClick={() => window.open(bankVerification.attachmentUrl, '_blank')}>
                                  View Uploaded Document
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Row 1: Customer Name ── */}
                    <Row>
                      <Col md={showConfirmAccountField ? 12 : 6}>
                        <div className='form-group mb-3'>
                          <label className='form-label'>Customer Name</label>
                          <Field
                            type='text'
                            name='nameAsOnBankAccount'
                            disabled
                            className='form-control'
                            onChange={(e: any) => {
                              const val = e.target.value.replace(/[^A-Za-z ]/g, '');
                              setFieldValue('nameAsOnBankAccount', val.toUpperCase());
                              setNewBankDetails((prev) => ({
                                ...prev,
                                nameAsOnBankAccount: val,
                              }));
                            }}
                          />
                          {errors.nameAsOnBankAccount && touched.nameAsOnBankAccount && (
                            <small>
                              <img src={infoIco} alt='' /> {getErrorMessage(errors.nameAsOnBankAccount)}
                            </small>
                          )}
                        </div>
                      </Col>
                      {/* </Row> */}

                      {/* ── Row 2: Account Number + Confirm Account Number ── */}
                      {/*
                    Layout rules:
                    • SUBMIT / EDIT  → Account No is col-md-6, Confirm Ac No is col-md-6 (side by side)
                    • All other states → Account No is col-md-6 only (confirm field not rendered)
                  */}
                      {/* <Row> */}
                      {/* Account Number – always col-md-6 (confirm is shown beside it when editable) */}
                      <Col md={6}>
                        <div className='form-group mb-3'>
                          <label className='form-label'>Account Number</label>

                          <div style={{ position: 'relative' }}>
                            {areFieldsEditable ? (
                              <div style={{ position: 'relative' }}>
                                <input
                                  type={showAccountNo ? 'text' : 'password'}
                                  name="bankAccount"
                                  disabled={!canEditFields || isAccountNoFrozen}
                                  className="form-control no-password-reveal"
                                  value={values.bankAccount}
                                  inputMode="numeric"
                                  autoComplete="new-password"
                                  autoCorrect="off"
                                  autoCapitalize="off"
                                  spellCheck={false}
                                  data-form-type="other"
                                  style={{
                                    paddingRight: '44px',
                                    MozAppearance: 'textfield' as React.CSSProperties['MozAppearance'],
                                  }}

                                  // 🔒 Strong prevention (before value enters)
                                  onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const nativeEvent = e.nativeEvent as InputEvent;

                                    if (nativeEvent?.inputType === 'insertFromPaste') {
                                      e.preventDefault();
                                    }
                                  }}

                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isUiLocked) return;
                                    if (isAccountNoFrozen) return;

                                    const nativeEvent = e.nativeEvent as InputEvent | undefined;

                                    // 🔒 Block paste / autofill injections
                                    if (nativeEvent?.inputType === 'insertFromPaste') {
                                      return;
                                    }

                                    // ✅ Original logic (unchanged)
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 18);

                                    setFieldValue('bankAccount', val);
                                    setNewBankDetails((prev: any) => ({
                                      ...prev,
                                      bankAccount: val,
                                    }));
                                    resetConfirmAccount();
                                  }}

                                  // 🔒 Fallback protections (for older browsers / edge cases)
                                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                                  onCopy={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                                  onCut={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                                  onDrop={(e: React.DragEvent<HTMLInputElement>) => e.preventDefault()}
                                  onContextMenu={(e: React.MouseEvent<HTMLInputElement>) => e.preventDefault()}
                                />
                              </div>
                            ) : (
                              /* Read-only / display: show masked value — last 3 digits visible */
                              <input
                                type='text'
                                readOnly
                                disabled
                                className='form-control'
                                value={maskAccountNo(values.bankAccount)}
                                style={{
                                  paddingRight:
                                    customer?.bankDetails?.state === 'PENDING' ||
                                      customer?.bankDetails?.state === 'REJECTED'
                                      ? '130px'
                                      : undefined,
                                  letterSpacing: '0.12em',
                                  fontFamily: 'monospace',
                                }}
                              />
                            )}

                            {/* PENDING / REJECTED badge — shown in read-only mode */}
                            {!areFieldsEditable &&
                              (customer?.bankDetails?.state === 'PENDING' ||
                                customer?.bankDetails?.state === 'REJECTED') && (
                                <span
                                  style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 16px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    letterSpacing: '0.6px',
                                    textTransform: 'uppercase',
                                    borderTopRightRadius: '4px',
                                    borderBottomRightRadius: '4px',
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                    color: '#fde2e2',
                                    backgroundColor: '#ba2f3dff',
                                    borderLeft: '1px solid #ffeeba',
                                  }}
                                >
                                  UNVERIFIED
                                </span>
                              )}
                          </div>

                          {errors.bankAccount && touched.bankAccount && (
                            <small>
                              <img src={infoIco} alt='' /> {getErrorMessage(errors.bankAccount)}
                            </small>
                          )}
                        </div>
                      </Col>

                      {/* ── Confirm Account Number (only on SUBMIT / EDIT) ── */}
                      {showConfirmAccountField && (
                        <Col md={6}>
                          <div className='form-group mb-3'>
                            <label className='form-label'>
                              Confirm Account Number
                            </label>

                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                value={confirmAccountNo}
                                disabled={!isConfirmFieldEnabled}
                                placeholder={
                                  isConfirmFieldEnabled
                                    ? 'Re-enter account number'
                                    : 'Enter account number first'
                                }
                                className={`form-control${confirmMismatch ? ' is-invalid' : ''
                                  }${confirmMatch ? ' is-valid' : ''}`}
                                style={{
                                  cursor: !isConfirmFieldEnabled ? 'not-allowed' : 'text',
                                  backgroundColor: !isConfirmFieldEnabled ? '#f8f9fa' : undefined,
                                  paddingRight:
                                    confirmMismatch || confirmMatch ? '40px' : undefined,
                                }}

                                inputMode="numeric"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                data-form-type="other"

                                // 🔒 Strong prevention (before input enters)
                                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                  const nativeEvent = e.nativeEvent as InputEvent;

                                  if (nativeEvent?.inputType === 'insertFromPaste') {
                                    e.preventDefault();
                                  }
                                }}

                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  if (!isConfirmFieldEnabled || isUiLocked) return;

                                  const nativeEvent = e.nativeEvent as InputEvent | undefined;

                                  // 🔒 Block paste / autofill injection
                                  if (nativeEvent?.inputType === 'insertFromPaste') {
                                    return;
                                  }

                                  const val = e.target.value.replace(/\D/g, '').slice(0, 18);

                                  setConfirmAccountNo(val);

                                  // Mark touched as soon as user starts typing
                                  if (!confirmAccountNoTouched) {
                                    setConfirmAccountNoTouched(true);
                                  }

                                  // Freeze primary account number when user starts typing
                                  if (val.length > 0 && !isAccountNoFrozen) {
                                    setIsAccountNoFrozen(true);
                                  }

                                  // Unfreeze if cleared
                                  if (val.length === 0) {
                                    setIsAccountNoFrozen(false);
                                    setConfirmAccountNoTouched(false);
                                  }
                                }}

                                // 🔒 Fallback protections
                                onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                  e.preventDefault()
                                }
                                onCopy={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                  e.preventDefault()
                                }
                                onCut={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                  e.preventDefault()
                                }
                                onDrop={(e: React.DragEvent<HTMLInputElement>) =>
                                  e.preventDefault()
                                }
                                onContextMenu={(e: React.MouseEvent<HTMLInputElement>) =>
                                  e.preventDefault()
                                }
                              />

                            </div>

                            {/* Validation messages */}
                            {confirmMismatch && (
                              <small className=' d-flex align-items-center gap-1 mt-1'>
                                <img src={infoIco} alt='' /> Account numbers do not match
                              </small>
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>

                    {/* ── Row 3: IFSC + Bank Name ── */}
                    <Row>
                      <Col md={6}>
                        <div className='form-group mb-3 position-relative'>
                          <label className='form-label fw-semibold text-dark'>
                            IFSC Code
                          </label>

                          <div className='input-group'>
                            <Field name='ifsc'>
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type='text'
                                  disabled={!canEditFields}
                                  placeholder='Enter IFSC Code'
                                  className='form-control'
                                  value={newBankDetails.ifsc || ''}
                                  onChange={(e) => {
                                    if (isUiLocked) return;
                                    const input = e.target.value.toUpperCase();
                                    if (!/^[A-Z0-9]*$/.test(input)) return;
                                    if (input.length > 11) return;

                                    setFieldValue('ifsc', input);
                                    setFieldValue('bankName', '');

                                    setNewBankDetails((prev) => ({
                                      ...prev,
                                      ifsc: input,
                                      bankName: '',
                                    }));

                                    setIsIfscVerified(false);
                                    setIfscApiError(null);
                                  }}
                                />
                              )}
                            </Field>

                            <div
                              className='d-flex align-items-center bg-white border border-1 rounded-end'
                              style={{ borderColor: '#ced4da', borderLeft: 'none' }}
                            >
                              {isIfscVerified ? (
                                <div className='px-2 d-flex align-items-center'>
                                  <img src={Verified} width='24' height='24' alt='Verified' />
                                </div>
                              ) : (
                                areFieldsEditable && !isIfscVerified && (
                                  <Button
                                    type='button'
                                    className='btn fw-semibold'
                                    onClick={() =>
                                      verifyIfscCode(newBankDetails.ifsc, setFieldValue, setErrors)
                                    }
                                    disabled={
                                      isUiLocked ||
                                      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(newBankDetails.ifsc)
                                    }
                                    style={{
                                      backgroundColor: isBankSearching ? '#e9e9e9' : '#99834D',
                                      color: '#fff',
                                      border: '1px solid #ced4da',
                                      borderLeft: 'none',
                                      borderRadius: '0 0.375rem 0.375rem 0',
                                      height: 'calc(2.25rem + 2px)',
                                      padding: '0 1rem',
                                    }}
                                  >
                                    {isBankSearching ? <Spinner animation='border' size='sm' /> : 'Search'}
                                  </Button>
                                )
                              )}
                            </div>
                          </div>

                          {!isIfscVerified && newBankDetails.ifsc && (
                            <>
                              <ul className='small mt-2 mb-1 ps-3' style={{ listStyle: 'none' }}>
                                {[
                                  {
                                    text: 'First 4 characters must be alphabets (A–Z)',
                                    valid: /^[A-Z]{4}/.test(newBankDetails.ifsc),
                                  },
                                  {
                                    text: '5th character must be the number 0',
                                    valid: newBankDetails.ifsc.length >= 5 && newBankDetails.ifsc[4] === '0',
                                  },
                                  {
                                    text: 'IFSC code must be exactly 11 characters long',
                                    valid: newBankDetails.ifsc.length === 11,
                                  },
                                ].map((rule, index) => (
                                  <li
                                    key={index}
                                    className='d-flex align-items-center mb-2'
                                    style={{ color: rule.valid ? '#198754' : '#dc3545', fontWeight: 500 }}
                                  >
                                    {rule.valid ? (
                                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#198754' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='me-2'>
                                        <circle cx='12' cy='12' r='10' stroke='#198754' fill='none' />
                                        <path d='M8 12.5l2.5 2.5L16 9.5' stroke='#198754' />
                                      </svg>
                                    ) : (
                                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#dc3545' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='me-2'>
                                        <circle cx='12' cy='12' r='10' stroke='#dc3545' fill='none' />
                                        <line x1='15' y1='9' x2='9' y2='15' stroke='#dc3545' />
                                        <line x1='9' y1='9' x2='15' y2='15' stroke='#dc3545' />
                                      </svg>
                                    )}
                                    {rule.text}
                                  </li>
                                ))}

                                {ifscApiError && (
                                  <li className='d-flex align-items-center' style={{ color: '#dc3545' }}>
                                    {ifscApiError}
                                  </li>
                                )}
                              </ul>

                              <div className='mt-2'>
                                <a
                                  href='https://www.rbi.org.in/Scripts/IFSCMICRDetails.aspx'
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-primary small'
                                >
                                  Search for your IFSC code here
                                </a>
                              </div>
                            </>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className='form-group mb-3'>
                          <label className='form-label'>Bank Name</label>
                          <Field type='text' name='bankName' readOnly className='form-control' disabled />
                        </div>
                      </Col>
                    </Row>

                    {/* ── Upload Bank Proof (UPLOAD_DOC state) ── */}
                    {uiAction === 'UPLOAD_DOC' && (
                      <div className='mt-3'>
                        <label className='form-label fw-semibold small mb-2'>
                          Upload Bank Proof
                          <span className='text-muted small ms-1'>
                            (Cancelled cheque / Bank statement)
                          </span>
                        </label>

                        {!bankProofFile && (
                          <div
                            className='border rounded p-3 text-center'
                            style={{
                              cursor: isUiLocked ? 'not-allowed' : 'pointer',
                              opacity: isUiLocked ? 0.6 : 1,
                            }}
                            onClick={() => {
                              if (isUiLocked) return;
                              document.getElementById('bankProofInput')?.click();
                            }}
                          >
                            <p className='mb-1 small fw-semibold'>Click to upload</p>
                            <p className='small text-muted mb-0'>JPG, PNG, WEBP, HEIC — max 3MB</p>

                            <input
                              id='bankProofInput'
                              type='file'
                              hidden
                              disabled={isUiLocked}
                              accept='image/*,.heic,.heif'
                              onChange={(e) => {
                                if (isUiLocked) return;
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 3 * 1024 * 1024) {
                                  toast.error('File size should not exceed 3MB');
                                  return;
                                }
                                handleBankProofSelect(file);
                              }}
                            />
                          </div>
                        )}

                        {bankProofFile && bankProofPreview && (
                          <div className='mt-2 border rounded p-2'>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                              <span className='small fw-semibold text-truncate'>{bankProofFile.name}</span>
                              <Button
                                size='sm'
                                variant='outline-danger'
                                onClick={() => {
                                  setBankProofFile(null);
                                  setBankProofPreview(null);
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                            <img
                              src={bankProofPreview}
                              alt='Bank Proof Preview'
                              className='img-fluid rounded'
                              style={{ maxHeight: 180 }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Consent + Submit ── */}
                    <div className='mt-4'>
                      {/* {requiresConsent && (
                      <div className='form-check mb-3'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='bankConsent'
                          checked={hasConsent}
                          onChange={(e) => setHasConsent(e.target.checked)}
                        />
                        <label
                          className='form-check-label small text-muted'
                          htmlFor='bankConsent'
                        >
                          I confirm that the bank details provided above are correct and belong to me. I authorize verification of these details.
                        </label>
                      </div>
                    )} */}

                      <div className='d-flex justify-content-end'>
                        {buttonConfig && (
                          <Button
                            type='button'
                            variant={buttonConfig.variant}
                            disabled={buttonConfig.disabled || isProcessing}
                            onClick={buttonConfig.onClick}
                            style={primaryBtnStyle(buttonConfig.variant)}
                            className='d-flex align-items-center justify-content-center gap-2'
                          >
                            {isProcessing ? (
                              <>
                                <Spinner size='sm' />
                                <span>Processing…</span>
                              </>
                            ) : (
                              buttonConfig.label
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>

            <ConfirmBankDetailsModal
              show={showConfirmModal}
              onClose={() => setShowConfirmModal(false)}
              isSubmitting={isProcessing}
              bankDetails={{
                nameAsOnBankAccount: newBankDetails.nameAsOnBankAccount,
                bankAccount: newBankDetails.bankAccount,
                ifsc: newBankDetails.ifsc,
                bankName: newBankDetails.bankName,
              }}
              onConfirm={() => {
                setShowConfirmModal(false);
                formikRef.current?.submitForm();
              }}
            />

            <ConfirmBankDocumentModal
              show={showUploadModal}
              onClose={() => setShowUploadModal(false)}
              isSubmitting={isProcessing}
              bankProofFile={bankProofFile}
              bankProofPreview={bankProofPreview}
              onConfirm={handleUploadBankProof}
            />
          </Card.Body>
        </Card>

        {!isPanVerified && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255,255,255,0.75)',
              zIndex: 10,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
            }}
          >
            <div className='text-center p-4'>
              <svg width='40' height='40' viewBox='0 0 24 24' fill='none' className='mb-3'>
                <rect x='3' y='11' width='18' height='11' rx='2' stroke='#99834D' strokeWidth='2' />
                <path d='M7 11V7a5 5 0 0110 0v4' stroke='#99834D' strokeWidth='2' strokeLinecap='round' />
              </svg>
              <div className='fw-semibold' style={{ color: '#99834D', fontSize: 15 }}>
                Please verify your PAN to unlock Bank Details
              </div>
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default BankForm;